/* ================================================================
   <liquid-glass-navbar> —— 苹果同款渐进模糊顶栏
   与 <liquid-glass> 的 SVG 折射不同：渐变模糊是纯 CSS 方案，
   三层 backdrop-filter 叠加，每层带纵向渐隐 mask，模糊半径在垂直方向
   累加（顶部最实、底部消散趋近透明），再叠一层只做染色的渐变层。
   不依赖 backdrop-filter: url()，因此 Safari / Firefox / Chromium 全兼容。
   ================================================================ */

const HAS_DOM = typeof window !== 'undefined' && typeof document !== 'undefined'
const HTMLElementBase = HAS_DOM ? HTMLElement : class {}

const toNumber = (value, fallback) => {
  const parsed = typeof value === 'string' || typeof value === 'number' ? Number(value) : NaN
  return Number.isFinite(parsed) ? parsed : fallback
}
const clamp = (value, min, max) => Math.min(Math.max(value, min), max)
const toRatio = (value, fallback) => {
  if (typeof value === 'string' && value.trim().endsWith('%')) {
    return toNumber(value, fallback * 100) / 100
  }
  return toNumber(value, fallback)
}

export const LIQUID_GLASS_NAVBAR_DEFAULTS = Object.freeze({
  height: 60,
  blur: 10,
  saturation: 1.9,
  tint: '#ffffff',
  darkTint: '#121214',
  extend: 36,
  dark: false
})

export function normalizeLiquidGlassNavbarOptions(input = {}) {
  return {
    height: clamp(toNumber(input.height, LIQUID_GLASS_NAVBAR_DEFAULTS.height), 24, 200),
    blur: clamp(toNumber(input.blur, LIQUID_GLASS_NAVBAR_DEFAULTS.blur), 0, 40),
    saturation: clamp(toRatio(input.saturation, LIQUID_GLASS_NAVBAR_DEFAULTS.saturation), 0, 3),
    tint: typeof input.tint === 'string' && input.tint.trim()
      ? input.tint.trim()
      : LIQUID_GLASS_NAVBAR_DEFAULTS.tint,
    darkTint: typeof input.darkTint === 'string' && input.darkTint.trim()
      ? input.darkTint.trim()
      : LIQUID_GLASS_NAVBAR_DEFAULTS.darkTint,
    extend: clamp(toNumber(input.extend, LIQUID_GLASS_NAVBAR_DEFAULTS.extend), 0, 200),
    dark: input.dark === undefined
      ? LIQUID_GLASS_NAVBAR_DEFAULTS.dark
      : !(input.dark === false || input.dark === 'false')
  }
}

function readNavbarOptions(element) {
  return normalizeLiquidGlassNavbarOptions({
    height: element.getAttribute('height'),
    blur: element.getAttribute('blur'),
    saturation: element.getAttribute('saturation'),
    tint: element.getAttribute('tint'),
    darkTint: element.getAttribute('dark-tint'),
    extend: element.getAttribute('extend'),
    dark: element.getAttribute('dark') ?? undefined
  })
}

/* 三层渐隐 mask：覆盖范围逐层向顶部收拢，模糊因此自上而下累加 */
const BLUR_LAYERS = [
  'linear-gradient(180deg, #000 0%, rgba(0,0,0,0.7) 52%, rgba(0,0,0,0.28) 80%, transparent 100%)',
  'linear-gradient(180deg, #000 0%, rgba(0,0,0,0.55) 30%, transparent 66%)',
  'linear-gradient(180deg, #000 0%, rgba(0,0,0,0.5) 14%, transparent 38%)'
]

/* 染色层：不带模糊，只把底色按同样节奏自上而下压下去 */
const tintGradient = (rgb, dark) => {
  const stops = dark ? [0.88, 0.48, 0.14] : [0.86, 0.5, 0.16]
  return `linear-gradient(180deg, rgba(${rgb},${stops[0]}) 0%, rgba(${rgb},${stops[1]}) 42%, rgba(${rgb},${stops[2]}) 72%, transparent 100%)`
}

function hexToRgbTuple(hex) {
  const normalized = hex.trim().replace('#', '')
  const full = normalized.length === 3
    ? normalized.split('').map((c) => c + c).join('')
    : normalized.padEnd(6, '0').slice(0, 6)
  const int = parseInt(full, 16)
  if (!Number.isFinite(int)) return null
  return `${(int >> 16) & 255},${(int >> 8) & 255},${int & 255}`
}

export class LiquidGlassNavbarElement extends HTMLElementBase {
  static get observedAttributes() {
    return ['height', 'blur', 'saturation', 'tint', 'dark-tint', 'extend', 'dark']
  }

  constructor() {
    super()
    if (!HAS_DOM || !this.attachShadow) return
    this.attachShadow({ mode: 'open' })
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          position: fixed;
          top: 0;
          left: 50%;
          translate: -50% 0;
          width: 100%;
          z-index: 9999;
          height: var(--lg-nav-height, 60px);
        }
        .blur-wrap {
          position: absolute;
          left: 0; right: 0; top: 0;
          bottom: calc(-1 * var(--lg-nav-extend, 36px));
          pointer-events: none;
          z-index: 0;
        }
        .layer, .tint { position: absolute; inset: 0; }
        .layer {
          backdrop-filter: blur(var(--lg-nav-blur, 10px)) saturate(var(--lg-nav-sat, 190%));
          -webkit-backdrop-filter: blur(var(--lg-nav-blur, 10px)) saturate(var(--lg-nav-sat, 190%));
        }
        .l1 { -webkit-mask: ${BLUR_LAYERS[0]}; mask: ${BLUR_LAYERS[0]}; }
        .l2 { -webkit-mask: ${BLUR_LAYERS[1]}; mask: ${BLUR_LAYERS[1]}; }
        .l3 { -webkit-mask: ${BLUR_LAYERS[2]}; mask: ${BLUR_LAYERS[2]}; }
        .tint { background: var(--lg-nav-tint); }
        .content {
          position: relative;
          z-index: 1;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      </style>
      <div class="blur-wrap" aria-hidden="true">
        <span class="layer l1"></span>
        <span class="layer l2"></span>
        <span class="layer l3"></span>
        <span class="tint"></span>
      </div>
      <div class="content" part="content"><slot></slot></div>
    `
  }

  connectedCallback() {
    this._apply()
  }

  attributeChangedCallback() {
    this._apply()
  }

  get options() {
    return readNavbarOptions(this)
  }

  configure(options = {}) {
    const map = {
      height: 'height',
      blur: 'blur',
      saturation: 'saturation',
      tint: 'tint',
      darkTint: 'dark-tint',
      extend: 'extend',
      dark: 'dark'
    }
    Object.entries(map).forEach(([key, attribute]) => {
      if (options[key] !== undefined) this.setAttribute(attribute, String(options[key]))
    })
    return this
  }

  _apply() {
    if (!this.shadowRoot) return
    const options = this.options
    this.style.setProperty('--lg-nav-height', `${options.height}px`)
    this.style.setProperty('--lg-nav-blur', `${options.blur}px`)
    this.style.setProperty('--lg-nav-sat', `${Math.round(options.saturation * 100)}%`)
    this.style.setProperty('--lg-nav-extend', `${options.extend}px`)

    const base = options.dark ? options.darkTint : options.tint
    const rgb = hexToRgbTuple(base) || '255,255,255'
    this.style.setProperty('--lg-nav-tint', tintGradient(rgb, options.dark))
  }
}

export function defineLiquidGlassNavbar(tagName = 'liquid-glass-navbar') {
  if (!HAS_DOM || !window.customElements) return null
  if (!window.customElements.get(tagName)) {
    window.customElements.define(tagName, LiquidGlassNavbarElement)
  }
  return window.customElements.get(tagName)
}
